// =============================================================================
// MigrationPanel — krok „Migracja" w mapie = SKROT do Studio (/strony/:websiteId).
// Tu tylko: zalozenie migracji (wymaga mapy z domena) + status + „Otworz studio".
// Cala reszta (motyw, design, podglad, zgodnosc, deploy) zyje w Studio.
// =============================================================================

import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, ArrowRightLeft, ExternalLink, ArrowRight } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useStaticSite } from "@/hooks/useStaticSite";
import { useWebsites } from "@/hooks/useWebsites";
import type { ThematicMap } from "@/types/thematicMaps";
import { StaticSiteStatusBadge, engineLabel } from "@/components/static-sites/statusBadge";

export function MigrationPanel({ map }: { map: ThematicMap }) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { websites } = useWebsites();
  const website = useMemo(() => websites.find((w) => w.id === map.website_id) || null, [websites, map.website_id]);
  const { site, loading, error, ensure } = useStaticSite({ mapId: map.id });
  const [creating, setCreating] = useState(false);

  const handleStart = async () => {
    if (!map.website_id || !website?.url) return;
    setCreating(true);
    try {
      const row = await ensure({ websiteId: map.website_id, websiteUrl: website.url, workspace: map.workspace || "firma" });
      toast({ title: "Migracja założona", description: "Otwieram Studio." });
      navigate(`/strony/${row.website_id}`);
    } catch (e) {
      toast({ title: "Nie udało się założyć migracji", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="flex items-center gap-2 text-base font-semibold"><ArrowRightLeft className="h-4 w-4" /> Migracja strony</h3>
        <p className="text-sm text-muted-foreground">WordPress → strona statyczna na Cloudflare Pages. Te same adresy, ta sama treść, bez CMS.</p>
      </div>

      {error && <Card className="border-destructive/40"><CardContent className="py-3 text-sm text-destructive">{error}</CardContent></Card>}

      {!map.website_id ? (
        <Card><CardContent className="py-6 text-sm text-muted-foreground">Mapa nie ma przypisanej domeny. Przypisz ją w ustawieniach mapy, żeby móc migrować.</CardContent></Card>
      ) : !site ? (
        <Card>
          <CardContent className="flex flex-wrap items-center justify-between gap-3 py-5">
            <div className="text-sm">
              <div className="font-medium">Domena: <span className="font-mono">{website?.url || "…"}</span></div>
              <div className="text-muted-foreground">Załóż migrację, żeby otworzyć Studio (motyw, design, podgląd, deploy). To tylko wpis w panelu, nic nie zmienia na stronie.</div>
            </div>
            <Button onClick={() => void handleStart()} disabled={creating || loading || !website?.url}>
              {creating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ArrowRightLeft className="mr-2 h-4 w-4" />}
              Załóż migrację tej domeny
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-primary/30 bg-primary/5">
          <CardContent className="flex flex-wrap items-center gap-3 py-5">
            <div className="min-w-0 flex-1 text-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono font-medium">{site.domain}</span>
                <StaticSiteStatusBadge status={site.status} />
                <span className="text-xs text-muted-foreground">{engineLabel(site)}</span>
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                {site.pages_subdomain && <a href={`https://${site.pages_subdomain}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 underline">{site.pages_subdomain} <ExternalLink className="h-3 w-3" /></a>}
                <span>Motyw, design AI, podgląd, zgodność i deploy są w Studio.</span>
              </div>
            </div>
            <Button onClick={() => navigate(`/strony/${site.website_id}`)}>Otwórz studio <ArrowRight className="ml-2 h-4 w-4" /></Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
